import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileUp, Upload } from 'lucide-react';
import { useEffect, useState, type DragEvent, type FormEvent } from 'react';
import { errorMessage } from '../lib/api';
import {
  DOCUMENT_KIND_LABEL,
  formatBytes,
  MAX_DOCUMENT_BYTES,
  uploadDocument,
  useFacilityPlaces,
  type DocumentInput,
  type FacilityDocument,
  type FacilityDocumentKind,
} from '../lib/facilities';
import { EquipmentSelect } from './facilities-ui';
import { useToast } from './toast';
import { Alert, Button, Field, Input, Modal, Select, Textarea, cn } from './ui';

/** "Boiler manual.pdf" → "Boiler manual" */
const withoutExtension = (fileName: string) => (fileName.replace(/\.[^.]+$/, '') || fileName).slice(0, 200);

export function DocumentUploadModal({
  open,
  onClose,
  defaults,
  onUploaded,
}: {
  open: boolean;
  onClose(): void;
  /** Preset details. With an equipment, inspection, or work order link, the link fields are hidden. */
  defaults?: Partial<DocumentInput>;
  onUploaded?(document: FacilityDocument): void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const linked = !!(defaults?.facilityAssetId || defaults?.inspectionId || defaults?.workOrderId);
  const places = useFacilityPlaces(open && !linked);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<FacilityDocumentKind>('OTHER');
  const [tags, setTags] = useState('');
  const [description, setDescription] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [facilityAssetId, setFacilityAssetId] = useState('');
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setTitle(defaults?.title ?? '');
    setKind(defaults?.kind ?? 'OTHER');
    setTags(defaults?.tags?.join(', ') ?? '');
    setDescription(defaults?.description ?? '');
    setBuildingId(defaults?.buildingId ?? '');
    setFacilityAssetId('');
    setDragging(false);
    setError('');
    // Callers pass `defaults` inline, so only opening the modal resets the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = (next: File | null | undefined) => {
    if (!next) return;
    if (next.size > MAX_DOCUMENT_BYTES) {
      setFile(null);
      setError(`${next.name} is ${formatBytes(next.size)}. Documents can be up to 25 MB.`);
      return;
    }
    if (next.size === 0) {
      setFile(null);
      setError(`${next.name} is empty. Choose another file.`);
      return;
    }
    setError('');
    setFile(next);
  };

  const onDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  };

  const onDragLeave = (event: DragEvent<HTMLLabelElement>) => {
    // Moving over the zone's own text and icon isn't leaving it.
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
  };

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(false);
    choose(event.dataTransfer.files?.[0]);
  };

  const mutation = useMutation({
    mutationFn: (upload: { file: File; input: DocumentInput }) => uploadDocument(upload.file, upload.input),
    onSuccess: (document) => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success(`Uploaded ${document.title}`);
      onUploaded?.(document);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError('Choose a file to upload');
      return;
    }
    setError('');
    const links: Partial<DocumentInput> = linked
      ? {
          facilityAssetId: defaults?.facilityAssetId || undefined,
          buildingId: defaults?.buildingId || undefined,
          inspectionId: defaults?.inspectionId || undefined,
          workOrderId: defaults?.workOrderId || undefined,
        }
      : { buildingId: buildingId || undefined, facilityAssetId: facilityAssetId || undefined };
    mutation.mutate({
      file,
      input: {
        ...links,
        title: title.trim() || withoutExtension(file.name),
        kind,
        description: description.trim() || null,
        tags: [
          ...new Set(
            tags
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean),
          ),
        ],
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload a document"
      description="Manuals, warranties, maintenance logs, inspection reports, and drawings, up to 25 MB."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="document-upload-form" icon={<Upload />} disabled={!file} loading={mutation.isPending}>
            Upload
          </Button>
        </>
      }
    >
      <form id="document-upload-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}

        <label
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors',
            dragging ? 'border-brand-500 bg-brand-50/60' : 'border-slate-300 hover:border-brand-400 hover:bg-brand-50/30',
          )}
        >
          <FileUp className="size-8 text-slate-400" />
          <span className="mt-2 max-w-full truncate text-sm font-medium text-slate-800">{file ? file.name : 'Choose a file or drop it here'}</span>
          <span className="mt-1 text-xs text-slate-500">
            {file ? `${formatBytes(file.size)} · choose or drop another file to replace it` : 'PDFs, photos, spreadsheets, and other files up to 25 MB'}
          </span>
          <input
            type="file"
            className="sr-only"
            disabled={mutation.isPending}
            onChange={(event) => {
              choose(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </label>

        <Field label="Title" hint={file && !title.trim() ? 'Leave blank to use the file name' : undefined}>
          <Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} placeholder={file ? withoutExtension(file.name) : 'e.g. Boiler operation manual'} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type">
            <Select value={kind} onChange={(event) => setKind(event.target.value as FacilityDocumentKind)}>
              {(Object.keys(DOCUMENT_KIND_LABEL) as FacilityDocumentKind[]).map((value) => (
                <option key={value} value={value}>
                  {DOCUMENT_KIND_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tags" hint="Separate tags with commas">
            <Input value={tags} onChange={(event) => setTags(event.target.value)} placeholder="e.g. boiler, annual" />
          </Field>
        </div>

        {!linked && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Building">
              <Select
                value={buildingId}
                onChange={(event) => {
                  setBuildingId(event.target.value);
                  setFacilityAssetId('');
                }}
              >
                <option value="">No building</option>
                {places.data?.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Equipment">
              <EquipmentSelect value={facilityAssetId} onChange={setFacilityAssetId} buildingId={buildingId || undefined} />
            </Field>
          </div>
        )}

        <Field label="Description">
          <Textarea value={description} onChange={(event) => setDescription(event.target.value)} maxLength={2000} rows={3} />
        </Field>
      </form>
    </Modal>
  );
}
